import React from 'react';
import { inject, observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';

import { GetConceptListItemResponse, GetConceptResponse, Nullable } from 'chaika-frontend-types/frontend-cms';

import { AppState } from '@store/App/App';
import { SnomedPage } from './SnomedPage';
import { snomedService } from './snomedService';
import { OptionType } from '@components/Select';

interface StoreProps {
    app: AppState;
}

const injectableStores: (keyof StoreProps)[] = [AppState.Name];

@observer
class Component extends React.Component<StoreProps> {
    @observable private snomedCTName: Nullable<OptionType> = null;
    @observable private snomedICDName: Nullable<OptionType> = null;
    @observable private isICDDisabled: boolean = true;
    @observable private snomedCTDefaultOptions: OptionType[] = [];
    @observable private snomedICOptions: OptionType[] = [];

    constructor(props: StoreProps) {
        super(props);
        makeObservable(this);
    }

    public componentDidMount(): void {
        const { app } = this.props;
        app.setGreyBackground(true);
    }

    public render(): React.ReactNode {
        return React.createElement(SnomedPage, {
            snomedCTName: this.snomedCTName,
            snomedICDName: this.snomedICDName,
            snomedCTDefaultOptions: this.snomedCTDefaultOptions,
            snomedICOptions: this.snomedICOptions,
            isICDDisabled: this.isICDDisabled,
            onSearchLoadOptions: this.handleSearchLoadOptions,
            onSearchChange: this.handleSearchChange,
            onIcdChange: this.handleIcdChange,
        });
    }

    @action.bound
    private async handleSearchLoadOptions(inputValue: string) {
        if (inputValue.length >= 4) {
            const res = await snomedService.getList(inputValue);
            this.snomedCTDefaultOptions = this.makeSnomedCTOptions(res);
            return this.makeSnomedCTOptions(res);
        }
        return [];
    }

    @action.bound
    private async handleSearchChange(params: OptionType) {
        this.snomedCTName = params;
        this.snomedICDName = null;

        const res = await snomedService.getById(String(params.value));
        this.snomedICOptions = this.makeSnomedICDOptions(res);
    }

    private makeSnomedCTOptions(data: GetConceptListItemResponse[]): OptionType[] {
        return data.map(({ id, foundDescription: { translations: primaryTranslations } }) => {
            return {
                label: primaryTranslations[0]?.term,
                value: id,
                additionalText: primaryTranslations[1]?.term ?? '',
            };
        });
    }

    private makeSnomedICDOptions(data: GetConceptResponse): OptionType[] {
        this.isICDDisabled = !data.icd10Codes.length;
        return data.icd10Codes.map(({ code, translations }) => {
            return {
                label: translations[0]?.text,
                value: data.id,
                additionalText: translations[1]?.text ?? '',
                highlightText: code,
            };
        });
    }

    @action.bound
    private handleIcdChange(params: OptionType) {
        this.snomedICDName = params;
    }
}

export const SnomedPageContainer = inject<{}, StoreProps>(...injectableStores)(Component);
